import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import type { GiftConfig, GiftStep, ConditionRule } from '../types/gift-config'
import { fetchGiftConfig, submitGiftAnswers } from '../api/gift'
import type { GiftSubmitRequest } from '../api/gift'

export const useGiftModal = defineStore('giftModal', () => {
  const giftModal = ref({
    isShow: false,
    currentStepIndex: 0,
    isLoading: false,
    isLoadingConfig: false,
    config: null as GiftConfig | null,
    giftData: {} as Record<string, any>,
    resultLink: null as string | null
  })

  const steps = computed(() => giftModal.value.config?.steps ?? [])
  const currentStep = computed(() => steps.value[giftModal.value.currentStepIndex])
  const totalSteps = computed(() => steps.value.length)

  const getCurrentStep = (): GiftStep | undefined => {
    return currentStep.value
  }

  const getStepById = (stepId: string): GiftStep | undefined => {
    return steps.value.find(step => step.id === stepId)
  }

  const getStepIndexById = (stepId: string): number => {
    return steps.value.findIndex(step => step.id === stepId)
  }

  const evaluateConditionRule = (rule: ConditionRule, giftData: Record<string, any>): boolean => {
    const fieldValue = giftData[rule.field]

    switch (rule.operator) {
      case 'equals':
        return fieldValue === rule.value
      case 'not_equals':
        return fieldValue !== rule.value
      case 'greater_than':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue > rule.value
      case 'less_than':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue < rule.value
      case 'greater_or_equal':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue >= rule.value
      case 'less_or_equal':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue <= rule.value
      case 'in':
        return Array.isArray(rule.value) && rule.value.includes(fieldValue)
      case 'not_in':
        return Array.isArray(rule.value) && !rule.value.includes(fieldValue)
      case 'exists':
        return fieldValue !== null && fieldValue !== undefined && fieldValue !== ''
      case 'not_exists':
        return fieldValue === null || fieldValue === undefined || fieldValue === ''
      default:
        return false
    }
  }

  const shouldStepBeVisible = (step: GiftStep): boolean => {
    if (!step.conditional) return true

    const condition = step.conditional
    const data = giftModal.value.giftData

    // Поддержка старого формата для обратной совместимости
    if (condition.field && condition.value !== undefined) {
      return data[condition.field] === condition.value
    }

    // Новый формат с правилами
    if (condition.rules && condition.rules.length > 0) {
      const logic = condition.logic || 'AND'

      if (logic === 'AND') {
        return condition.rules.every(rule => evaluateConditionRule(rule, data))
      } else {
        return condition.rules.some(rule => evaluateConditionRule(rule, data))
      }
    }

    return true
  }

  const findNextVisibleStepIndex = (startIndex: number, direction: 'forward' | 'backward'): number => {
    if (direction === 'forward') {
      for (let i = startIndex + 1; i < steps.value.length; i++) {
        if (shouldStepBeVisible(steps.value[i])) {
          return i
        }
      }
    } else {
      for (let i = startIndex - 1; i >= 0; i--) {
        if (shouldStepBeVisible(steps.value[i])) {
          return i
        }
      }
    }
    return -1
  }

  const loadGiftConfig = async (config: GiftConfig) => {
    giftModal.value.config = config
    giftModal.value.giftData = {}
    giftModal.value.resultLink = null

    // Находим первый видимый шаг
    const firstVisibleIndex = findNextVisibleStepIndex(-1, 'forward')
    giftModal.value.currentStepIndex = firstVisibleIndex >= 0 ? firstVisibleIndex : 0
  }

  const validateCurrentStep = (): boolean => {
    const step = currentStep.value
    if (!step) return false

    if (step.required === false) return true

    if (step.type === 'recipient_info') {
      const name = giftModal.value.giftData[`${step.id}_name`]
      const relationship = giftModal.value.giftData[`${step.id}_relationship`]
      const gender = giftModal.value.giftData[`${step.id}_gender`]
      const age = giftModal.value.giftData[`${step.id}_age`]
      return name && relationship && gender && age
    }

    if (step.type === 'price_range') {
      const currency = giftModal.value.giftData[`${step.id}_currency`]
      const priceRange = giftModal.value.giftData[`${step.id}_priceRange`]
      return currency && priceRange
    }

    if (step.type === 'occasion') {
      const occasion = giftModal.value.giftData[step.id]
      if (!step.required) return true
      if (!occasion || occasion === null || occasion === undefined || occasion === '') return false
      // Проверяем, что для выбранного случая с subOptions выбрана дополнительная опция
      const occasionOption = step.occasions.find(occ => occ.value === occasion)
      if (occasionOption?.subOptions && occasionOption.subOptions.length > 0) {
        const subOptionValue = giftModal.value.giftData[`${step.id}_${occasion}`]
        if (!subOptionValue || subOptionValue === null || subOptionValue === undefined) {
          return false
        }
      }
      return true
    }

    if (step.type === 'brand_selection') {
      const value = giftModal.value.giftData[step.id]
      if (!step.required) return true
      return Array.isArray(value) && value.length > 0
    }

    if (step.type === 'style_likes') {
      const value = giftModal.value.giftData[step.id]
      if (!step.required) return true
      return Array.isArray(value) && value.length > 0
    }

    return true
  }

  const showGiftModal = async () => {
    giftModal.value.isShow = true
    giftModal.value.isLoadingConfig = true

    try {
      const config = await fetchGiftConfig()
      await loadGiftConfig(config)
    } catch (error) {
      console.error('Error loading gift config:', error)
    } finally {
      giftModal.value.isLoadingConfig = false
    }
  }

  const hideGiftModal = () => {
    giftModal.value.isShow = false
  }

  const nextStep = () => {
    if (!validateCurrentStep()) {
      return
    }

    const nextIndex = findNextVisibleStepIndex(giftModal.value.currentStepIndex, 'forward')
    if (nextIndex >= 0) {
      giftModal.value.currentStepIndex = nextIndex
    }
  }

  const prevStep = () => {
    const prevIndex = findNextVisibleStepIndex(giftModal.value.currentStepIndex, 'backward')
    if (prevIndex >= 0) {
      giftModal.value.currentStepIndex = prevIndex
    }
  }

  const goToStep = (stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < totalSteps.value) {
      const step = steps.value[stepIndex]
      // Если шаг не видим, находим ближайший видимый
      if (step && !shouldStepBeVisible(step)) {
        const visibleIndex = findNextVisibleStepIndex(stepIndex, 'forward')
        if (visibleIndex >= 0) {
          giftModal.value.currentStepIndex = visibleIndex
        } else {
          // Если вперед нет видимых, ищем назад
          const visibleIndexBack = findNextVisibleStepIndex(stepIndex, 'backward')
          if (visibleIndexBack >= 0) {
            giftModal.value.currentStepIndex = visibleIndexBack
          }
        }
      } else {
        giftModal.value.currentStepIndex = stepIndex
      }
    }
  }

  const goToStepById = (stepId: string) => {
    const index = getStepIndexById(stepId)
    if (index >= 0) {
      goToStep(index)
    }
  }

  const updateGiftData = (key: string, value: any) => {
    giftModal.value.giftData[key] = value

    // Если текущий шаг стал невидимым из-за изменения данных, переходим к следующему видимому
    const currentStepValue = currentStep.value
    if (currentStepValue && !shouldStepBeVisible(currentStepValue)) {
      const nextVisibleIndex = findNextVisibleStepIndex(giftModal.value.currentStepIndex, 'forward')
      if (nextVisibleIndex >= 0) {
        giftModal.value.currentStepIndex = nextVisibleIndex
      } else {
        // Если вперед нет видимых, ищем назад
        const prevVisibleIndex = findNextVisibleStepIndex(giftModal.value.currentStepIndex, 'backward')
        if (prevVisibleIndex >= 0) {
          giftModal.value.currentStepIndex = prevVisibleIndex
        }
      }
    }
  }

  const submitGiftSurvey = async () => {
    giftModal.value.isLoading = true
    try {
      const config = giftModal.value.config
      if (!config) {
        throw new Error('Gift config is not loaded')
      }

      // Копируем данные для отправки
      const answers = { ...giftModal.value.giftData }

      // Преобразуем данные для всех шагов типа 'occasion'
      const occasionSteps = config.steps.filter(step => step.type === 'occasion')
      
      for (const step of occasionSteps) {
        const occasion = answers[step.id]
        
        // Если есть выбранный случай (может быть строка или массив для обратной совместимости)
        if (occasion) {
          // Если это массив (старый формат)
          if (Array.isArray(occasion) && occasion.length > 0) {
            // Преобразуем в массив объектов
            const transformedOccasions = occasion.map(occasionValue => {
              const subOptionKey = `${step.id}_${occasionValue}`
              const subOption = answers[subOptionKey]
              
              // Удаляем поле с subOption из answers
              delete answers[subOptionKey]
              
              // Возвращаем объект с value и subOption (если есть)
              if (subOption !== null && subOption !== undefined) {
                return {
                  value: occasionValue,
                  subOption: subOption
                }
              } else {
                return {
                  value: occasionValue
                }
              }
            })
            
            // Заменяем массив строк на массив объектов
            answers[step.id] = transformedOccasions
          } else if (typeof occasion === 'string') {
            // Новый формат: одно значение (строка)
            const subOptionKey = `${step.id}_${occasion}`
            const subOption = answers[subOptionKey]
            
            // Удаляем поле с subOption из answers
            delete answers[subOptionKey]
            
            // Преобразуем в массив объектов (API ожидает массив)
            if (subOption !== null && subOption !== undefined) {
              answers[step.id] = [{
                value: occasion,
                subOption: subOption
              }]
            } else {
              answers[step.id] = [{
                value: occasion
              }]
            }
          }
        }
      }

      // Формируем данные для отправки на бекенд
      const submitData: GiftSubmitRequest = {
        giftConfigId: config.id,
        answers,
        metadata: {
          completedAt: new Date().toISOString(),
          deviceType: typeof window !== 'undefined' && window.innerWidth <= 768 ? 'mobile' : 'desktop',
          language: 'ru'
        }
      }

      // Отправляем данные на бекенд
      const result = await submitGiftAnswers(submitData)

      // Сохраняем ссылку в store
      giftModal.value.resultLink = result.link
    } catch (error) {
      console.error('Error submitting gift survey:', error)
      throw error
    } finally {
      giftModal.value.isLoading = false
    }
  }

  return {
    giftModal,
    steps,
    currentStep,
    totalSteps,
    getCurrentStep,
    getStepById,
    getStepIndexById,
    shouldStepBeVisible,
    findNextVisibleStepIndex,
    loadGiftConfig,
    validateCurrentStep,
    showGiftModal,
    hideGiftModal,
    nextStep,
    prevStep,
    goToStep,
    goToStepById,
    updateGiftData,
    submitGiftSurvey
  }
})

export const useGiftModalStore = useGiftModal

